import { useCallback, useState } from "react";
import { getArrayParamValue, getParamValue } from "../api_utils/requests";
import type { RangeFilter } from "../api_utils/types";

export const useStringHandler = (
  searchParams: URLSearchParams,
  paramKey: string,
  allValue: string = undefined,
  defaultValue = "",
  { showAllValue = false, onlyShowNonDefault = false }: { showAllValue?: boolean, onlyShowNonDefault?: boolean } = {
    showAllValue: false, onlyShowNonDefault: false
  },
): {
  value: string;
  setValue: (value: string) => void;
  handleRemove: () => void;
// eslint-disable-next-line @typescript-eslint/max-params
} => {
  let initialValue = getParamValue(searchParams.get(paramKey), defaultValue);
  if (defaultValue && onlyShowNonDefault && initialValue === defaultValue) {
    initialValue = "";
  } else if (allValue && !showAllValue && initialValue === allValue) {
    initialValue = "";
  }
  const [value, setValue] = useState<string>(initialValue);

  const handleChange = useCallback(
    (newValue: string) => {
      if (defaultValue && onlyShowNonDefault && newValue === defaultValue) {
        setValue("");
      } else if (allValue && !showAllValue && newValue === allValue) {
        setValue("");
      } else {
        setValue(newValue);
      }
    },
    [allValue, showAllValue, onlyShowNonDefault, defaultValue],
  );

  const handleRemove = useCallback(() => {
    if (onlyShowNonDefault) {
      setValue("");
    } else {
      setValue(defaultValue);
    }
  }, [defaultValue, onlyShowNonDefault]);

  return {
    value,
    setValue: handleChange,
    handleRemove,
  };
};

// Custom hook for range filters
export const useRangeHandler = <E, T extends RangeFilter<E>>(
  searchParams: URLSearchParams,
  key: string,
  defaultValue: T = { more_than: undefined, less_than: undefined } as T,
): {
  value: T;
  setValue: (value: T) => void;
  handleRemove: () => void;
} => {
  const [value, setValue] = useState<T>({
    more_than: getParamValue<E>(searchParams.get(`${key}[more_than]`), defaultValue.more_than),
    less_than: getParamValue<E>(searchParams.get(`${key}[less_than]`), defaultValue.less_than),
  } as T);

  const handleRemove = useCallback(() => {
    setValue(defaultValue);
  }, [defaultValue]);

  return {
    value,
    setValue,
    handleRemove,
  };
};

export const useArrayHandler = <T>(
  searchParams: URLSearchParams,
  paramKey: string,
  defaultValue: T[] = [],
): {
  values: T[];
  setValues: (value: T[]) => void;
  handleAdd: (value: T) => void;
  handleRemove: () => void;
} => {
  const [values, setValues] = useState<T[]>(
    getArrayParamValue<T>(searchParams.getAll(`${paramKey}[]`), "string", defaultValue),
  );

  const handleAdd = useCallback((newItem: T) => {
    setValues((prev) => [...prev, newItem]);
  }, []);

  const handleRemove = useCallback(() => {
    setValues(defaultValue);
  }, [defaultValue]);

  return {
    values,
    setValues,
    handleAdd,
    handleRemove,
  };
};
